import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiBook, FiX, FiPlus } from 'react-icons/fi';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const ACCOUNT_TYPES = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];

const emptyForm = { entity: '', code: '', name: '', account_type: 'ASSET', tdhp_code: '', is_postable: true };

const ChartOfAccounts = () => {
  const [entities, setEntities] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [entityFilter, setEntityFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entRes, accRes] = await Promise.all([
        financeAPI.entities.list(),
        financeAPI.accounts.list(entityFilter ? { entity: entityFilter } : {}),
      ]);
      setEntities(entRes.data.results || entRes.data || []);
      setAccounts(accRes.data.results || accRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load the chart of accounts.');
    } finally {
      setLoading(false);
    }
  }, [entityFilter]);

  useEffect(() => { load(); }, [load]);

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await financeAPI.accounts.create({ ...form, entity: form.entity || null });
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (e2) {
      setError(e2?.response?.data?.error || JSON.stringify(e2?.response?.data) || 'Failed to create account.');
    } finally {
      setSaving(false);
    }
  };

  const entityName = (id) => entities.find((e) => e.id === id)?.name || '—';

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiBook size={22} />Chart of Accounts</h1>
          <p style={fs.pageSubtitle}>Hierarchical accounts per legal entity — mapped to Tekdüzen Hesap Planı codes where applicable.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <select value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} style={fs.select}>
            <option value="">All entities</option>
            {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <button onClick={() => setShowForm((v) => !v)} style={fs.primaryButton}><FiPlus size={14} />New Account</button>
          <button onClick={load} title="Refresh" style={fs.iconButton}>
            <FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </button>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submitForm} style={{ ...fs.card, marginBottom: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, alignItems: 'end' }}>
          <div>
            <div style={fs.label}>Entity</div>
            <select value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              <option value="">Consolidation-only</option>
              {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <div style={fs.label}>Code</div>
            <input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} style={{ ...fs.input, width: '100%' }} />
          </div>
          <div>
            <div style={fs.label}>Name</div>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ ...fs.input, width: '100%' }} />
          </div>
          <div>
            <div style={fs.label}>Type</div>
            <select value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })} style={{ ...fs.select, width: '100%' }}>
              {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <div style={fs.label}>TDHP Code (optional)</div>
            <input value={form.tdhp_code} onChange={(e) => setForm({ ...form, tdhp_code: e.target.value })} style={{ ...fs.input, width: '100%' }} />
          </div>
          <button type="submit" disabled={saving} style={fs.primaryButton}>{saving ? 'Saving…' : 'Create'}</button>
        </form>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : accounts.length === 0 ? (
        <div style={fs.emptyState}><FiBook size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No accounts yet.</div></div>
      ) : (
        <div style={fs.tableWrap}>
          <table style={fs.table}>
            <thead>
              <tr style={fs.tableHeadRow}>
                {['Code', 'Name', 'Entity', 'Type', 'Normal Balance', 'TDHP', 'Postable'].map((h) => <th key={h} style={fs.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {accounts.map((a, i) => (
                <tr key={a.id} style={{ borderBottom: i < accounts.length - 1 ? '1px solid #111' : 'none' }}>
                  <td style={{ ...fs.td, color: fs.colors.text, fontFamily: 'monospace' }}>{a.code}</td>
                  <td style={{ ...fs.td, color: fs.colors.textBright }}>{a.name}</td>
                  <td style={fs.td}>{a.entity ? entityName(a.entity) : <span style={{ color: fs.colors.textFaint }}>Consolidated</span>}</td>
                  <td style={fs.td}><span style={fs.badge(fs.colors.purple)}>{a.account_type}</span></td>
                  <td style={fs.td}>{a.normal_balance}</td>
                  <td style={{ ...fs.td, fontFamily: 'monospace' }}>{a.tdhp_code || '—'}</td>
                  <td style={fs.td}>{a.is_postable ? 'Yes' : 'No (summary)'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ChartOfAccounts;
